export const MAX_LIST_PAGE_SIZE = 200
export const DEFAULT_LIST_PAGE_SIZE = 10
export const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

/** Nearest allowed size. An equal distance keeps the smaller option. Garbage is 10. */
export function snapPageSize(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number.parseInt(String(raw), 10)
  if (!Number.isFinite(n)) return DEFAULT_LIST_PAGE_SIZE
  let best: number = PAGE_SIZE_OPTIONS[0]
  let bestDist = Infinity
  for (const opt of PAGE_SIZE_OPTIONS) {
    const dist = Math.abs(n - opt)
    if (dist < bestDist || (dist === bestDist && opt < best)) {
      best = opt
      bestDist = dist
    }
  }
  return best
}

/** Absent page_size is 10 and stays off the URL. A present value outside 10/20/50 is snapped. */
export function pageSizeFromSearch(raw: string | null): { size: number; rewrite: string | null } {
  if (raw == null || raw === '') return { size: DEFAULT_LIST_PAGE_SIZE, rewrite: null }
  const size = snapPageSize(raw)
  return { size, rewrite: raw === String(size) ? null : String(size) }
}

export function clampPageSize(size: number, max = MAX_LIST_PAGE_SIZE): number {
  const parsed = Math.floor(size)
  if (!Number.isFinite(parsed) || parsed < 1) return 1
  return Math.min(parsed, max)
}

export function maxPageForTotal(total: number, pageSize: number): number {
  if (pageSize < 1) return 1
  if (total <= 0) return 1
  return Math.max(1, Math.ceil(total / pageSize))
}

/** Clamp a Go-to-page value. Out of range still lands on a real page and explains which one. */
export function pageJumpMessage(raw: string, page: number, totalPages: number): { target: number; hint: string | null } {
  const last = Math.max(totalPages, 1)
  const parsed = Number.parseInt(raw, 10)
  if (!Number.isFinite(parsed)) return { target: page, hint: `Enter a page from 1 to ${last}` }
  const target = Math.min(Math.max(parsed, 1), last)
  if (parsed !== target) return { target, hint: `Page ${parsed} doesn't exist. Showing page ${target} of ${last}.` }
  return { target, hint: null }
}

export function clampPage(page: number, maxPage: number): number {
  const safeMax = Math.max(1, maxPage)
  return Math.min(Math.max(1, page), safeMax)
}

export function inferTotalFromPage(params: {
  page: number
  pageSize: number
  rowCount: number
  hasMore: boolean
}): number | null {
  const { page, pageSize, rowCount, hasMore } = params
  if (rowCount <= 0 || hasMore) return null
  return (Math.max(1, page) - 1) * pageSize + rowCount
}

export function resolveOffsetPagination(params: {
  page: number
  pageSize: number
  total: number | null | undefined
  rowCount: number
  hasMore: boolean
}): { safePage: number; totalItems: number; totalPages: number } {
  const page = Math.max(1, params.page)
  const pageSize = Math.max(1, params.pageSize)
  const inferred =
    params.total ??
    inferTotalFromPage({
      page,
      pageSize,
      rowCount: params.rowCount,
      hasMore: params.hasMore,
    }) ??
    0
  const totalPages = maxPageForTotal(inferred, pageSize)
  const safePage = clampPage(page, totalPages)
  return { safePage, totalItems: inferred, totalPages }
}
